import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, X, Clock, Trash2 } from "lucide-react";
import { productPath } from "../lib/productSlug";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECENT_KEY = "dj_recent_searches";
const RECENT_LIMIT = 5;

const loadRecent = () => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, RECENT_LIMIT) : [];
  } catch {
    return [];
  }
};

const saveRecent = (list) => {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_LIMIT))); } catch {}
};

const matchesQuery = (p, tokens) => {
  if (!tokens.length) return true;
  const haystack = [
    p.title,
    p.category,
    p.description,
    p.price != null ? `€ ${p.price}` : "",
    p.price != null ? String(p.price) : "",
    p.bestseller ? "bestseller best" : "",
    p.limited ? "limited limitata edizione" : "",
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return tokens.every((tok) => haystack.includes(tok));
};

export const SearchModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState(loadRecent);
  const inputRef = useRef(null);
  const persistTimerRef = useRef(null);

  // Load products once when first opened
  useEffect(() => {
    if (!open || products.length > 0) return;
    setLoading(true);
    axios.get(`${API}/products`)
      .then((r) => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, products.length]);

  // Autofocus + reset query each time the modal opens; refresh recent list
  useEffect(() => {
    if (open) {
      setQuery("");
      setRecent(loadRecent());
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Debounced persist: when user stops typing for 800ms, save query to recents
  useEffect(() => {
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    const q = query.trim();
    if (q.length < 2) return;
    persistTimerRef.current = setTimeout(() => {
      setRecent((prev) => {
        const next = [q, ...prev.filter((r) => r.toLowerCase() !== q.toLowerCase())].slice(0, RECENT_LIMIT);
        saveRecent(next);
        return next;
      });
    }, 800);
    return () => clearTimeout(persistTimerRef.current);
  }, [query]);

  // Lock body scroll + Esc to close
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const tokens = q.split(/\s+/).filter(Boolean);
    return products.filter((p) => matchesQuery(p, tokens));
  }, [products, query]);

  const removeRecent = (term) => {
    setRecent((prev) => {
      const next = prev.filter((r) => r !== term);
      saveRecent(next);
      return next;
    });
  };

  const clearAllRecent = () => {
    setRecent([]);
    saveRecent([]);
  };

  if (!open) return null;

  return (
    <div data-testid="search-modal" className="fixed inset-0 z-[90]">
      <div onClick={onClose} className="absolute inset-0 bg-black/90 backdrop-blur-md" />

      <div className="relative h-full overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-20 pb-16">
          {/* Close */}
          <button
            data-testid="search-modal-close"
            onClick={onClose}
            aria-label={t("search.close")}
            className="absolute top-6 right-6 p-3 text-white hover:bg-yellow-400 hover:text-black border-2 border-zinc-800 hover:border-yellow-400 transition"
          >
            <X className="w-5 h-5" strokeWidth={3} />
          </button>

          {/* Eyebrow */}
          <div className="font-jp text-yellow-400 text-xs tracking-widest mb-2">検索 / SEARCH</div>
          <h2 className="font-heading text-4xl sm:text-5xl uppercase text-white leading-none mb-6">
            {t("search.title_1")} <span className="text-yellow-400">{t("search.title_accent")}</span>
          </h2>

          {/* Input */}
          <div className="relative flex items-center">
            <Search className="absolute left-4 w-6 h-6 text-yellow-400 pointer-events-none" strokeWidth={3} />
            <input
              ref={inputRef}
              data-testid="search-modal-input"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("shop.search_placeholder")}
              className="w-full bg-zinc-900 border-2 border-yellow-400 text-white font-heading uppercase tracking-widest text-base sm:text-lg pl-14 pr-14 py-4 outline-none"
              aria-label={t("shop.search_placeholder")}
            />
            {query && (
              <button
                type="button"
                onClick={() => { setQuery(""); inputRef.current?.focus(); }}
                aria-label={t("shop.search_clear")}
                className="absolute right-3 p-2 text-zinc-400 hover:text-yellow-400 transition"
              >
                <X className="w-5 h-5" strokeWidth={3} />
              </button>
            )}
          </div>

          {/* Counter */}
          <div className="mt-3 text-xs font-mono uppercase tracking-widest text-zinc-500" data-testid="search-modal-count">
            {loading
              ? t("search.loading")
              : query
                ? t("shop.search_results", { count: results.length })
                : t("search.hint")}
          </div>

          {/* Recent searches — visible only when query is empty */}
          {!query && recent.length > 0 && (
            <div className="mt-6" data-testid="search-recent">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-xs font-heading uppercase tracking-widest text-zinc-400">
                  <Clock className="w-3.5 h-3.5 text-yellow-400" strokeWidth={3} />
                  {t("search.recent")}
                </div>
                <button
                  type="button"
                  data-testid="search-recent-clear-all"
                  onClick={clearAllRecent}
                  className="flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-zinc-500 hover:text-yellow-400 transition"
                >
                  <Trash2 className="w-3 h-3" /> {t("search.clear_all")}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {recent.map((term) => (
                  <div
                    key={term}
                    data-testid={`search-recent-chip-${term}`}
                    className="inline-flex items-center bg-zinc-900 border-2 border-zinc-800 hover:border-yellow-400 group transition"
                  >
                    <button
                      type="button"
                      onClick={() => { setQuery(term); inputRef.current?.focus(); }}
                      className="px-3 py-1.5 font-heading uppercase tracking-widest text-xs text-white group-hover:text-yellow-400 transition"
                    >
                      {term}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeRecent(term)}
                      aria-label={t("search.recent_remove")}
                      className="px-2 py-1.5 border-l-2 border-zinc-800 text-zinc-500 hover:text-red-400 hover:bg-zinc-800 transition"
                    >
                      <X className="w-3 h-3" strokeWidth={3} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Results */}
          <ul className="mt-6 space-y-2" data-testid="search-modal-results">
            {query && results.length === 0 && !loading && (
              <li className="text-center py-12 text-zinc-500 font-heading uppercase">{t("shop.search_empty")}</li>
            )}
            {results.map((p) => (
              <li key={p.id}>
                <Link
                  to={productPath(p)}
                  data-testid={`search-modal-result-${p.id}`}
                  onClick={onClose}
                  className="flex items-center gap-4 p-3 bg-zinc-900 border-2 border-zinc-800 hover:border-yellow-400 hover:bg-zinc-900/80 transition group"
                >
                  <img src={p.image} alt="" className="w-16 h-16 sm:w-20 sm:h-20 object-cover border-2 border-zinc-800 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-heading uppercase text-base text-white truncate group-hover:text-yellow-400 transition">{p.title}</div>
                    <div className="text-xs uppercase tracking-widest text-zinc-500 mt-0.5">{p.category}</div>
                    <div className="text-xs text-zinc-500 mt-1 line-clamp-1 hidden sm:block">{p.description}</div>
                  </div>
                  <div className="font-mono font-bold text-yellow-400 text-lg whitespace-nowrap">
                    € {p.price.toFixed(2)}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
